import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import type { EmployerFootprintAdminViewDto, EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import { concernReviewEvidenceTarget } from '../../lib/employerReview/buildWhatsHappeningReviewPathSteps';
import {
  findingSeverityChipProps,
  findingVisualTheme,
} from '../../lib/employerReview/findingVisualTheme';
import {
  findingBriefElementId,
  scrollToEmployerReviewTarget,
} from '../../lib/employerReview/employerReviewNavigate';
import type { ReviewReason } from '../../lib/employerReview/reviewReason.types';
import {
  concernAccordionDetailsSx,
  concernAccordionShellSx,
  concernAccordionSummarySx,
  resolveConcernAccent,
  severityChipSx,
  neutralGroupAccordionSx,
} from './concernAccordionStyles';
import { ConcernWhyWhatTeaser } from './ConcernWhyWhatTeaser';
import { ReviewEvidenceButton } from './guidedTour/ReviewEvidenceButton';
import { useEmployerEvidenceTourOptional } from './guidedTour/EmployerEvidenceTourContext';

export function ReviewConcernAccordion({
  reason,
  defaultExpanded = false,
  nested = false,
  primaryTourTarget,
}: {
  reason: ReviewReason;
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  defaultExpanded?: boolean;
  nested?: boolean;
  /** First concern in the summary list — wider guided-tour spotlight. */
  primaryTourTarget?: string;
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const nav = useEmployerEvidenceTourOptional();
  const tourActive = Boolean(nav?.reviewPathTour);
  const [userExpanded, setUserExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (!nav?.reviewPathTour && nav?.concernCollapseToken) {
      setUserExpanded(false);
    }
  }, [nav?.concernCollapseToken, nav?.reviewPathTour]);

  const expanded = tourActive
    ? nav?.expandedConcernId === reason.id
    : userExpanded;

  const visual = findingVisualTheme(reason.id, reason.severity, isLight);
  const chip = findingSeverityChipProps(reason.id, reason.severity, isLight);
  const accent = resolveConcernAccent(visual, reason.severity, theme, isLight);

  const whyText = reason.explanation;
  const whatText = reason.recommendedAction;

  const openFindingEvidence = () => {
    if (nav) {
      nav.openVerificationForFinding(reason.id);
      return;
    }
    scrollToEmployerReviewTarget(findingBriefElementId(reason.id));
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => {
        if (tourActive) {
          nav?.setExpandedConcernId(open ? reason.id : null);
          return;
        }
        setUserExpanded(open);
      }}
      disableGutters
      elevation={0}
      sx={concernAccordionShellSx(accent, { nested })}
      data-tour-target={primaryTourTarget}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 22 }} />}
        sx={concernAccordionSummarySx(accent)}
      >
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', width: '100%', pr: 0.5 }}>
          <Chip
            size="small"
            label={chip.label}
            color={chip.color}
            sx={chip.sx ?? severityChipSx(chip.color)}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4, flex: 1, minWidth: 0 }}>
            {reason.title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={concernAccordionDetailsSx()}>
        <Stack spacing={1.1}>
          <ConcernWhyWhatTeaser why={whyText} what={whatText} accent={accent.stripe} />

          {reason.sourceChecks.length > 0 ? (
            <Box data-tour-target={concernReviewEvidenceTarget(reason.id)}>
              <ReviewEvidenceButton
                compact
                accent={accent.stripe}
                onClick={openFindingEvidence}
              />
            </Box>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export function ReviewConcernList({
  primaryReasons,
  additionalReasons,
  detail = null,
  footprint = null,
}: {
  primaryReasons: ReviewReason[];
  additionalReasons: ReviewReason[];
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
}) {
  const nav = useEmployerEvidenceTourOptional();
  const [moreConcernsOpen, setMoreConcernsOpen] = useState(false);

  useEffect(() => {
    if (!nav?.reviewPathTour && nav?.concernCollapseToken) {
      setMoreConcernsOpen(false);
    }
  }, [nav?.concernCollapseToken, nav?.reviewPathTour]);

  if (!primaryReasons.length && !additionalReasons.length) return null;

  return (
    <Stack spacing={1} data-tour-target="employer-concern-list">
      {primaryReasons.map((reason, index) => (
        <ReviewConcernAccordion
          key={reason.id}
          reason={reason}
          detail={detail}
          footprint={footprint}
          defaultExpanded={index === 0}
          primaryTourTarget={index === 0 ? 'employer-concern-primary' : undefined}
        />
      ))}

      {additionalReasons.length > 0 ? (
        <Accordion
          expanded={moreConcernsOpen}
          onChange={(_, open) => setMoreConcernsOpen(open)}
          disableGutters
          elevation={0}
          sx={neutralGroupAccordionSx()}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 22 }} />}
            sx={{
              minHeight: 44,
              px: 1.5,
              '& .MuiAccordionSummary-content': { my: 0.45 },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {additionalReasons.length === 1
                ? '1 more concern'
                : `${additionalReasons.length} more concerns`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
            <Stack spacing={0.85}>
              {additionalReasons.map((reason) => (
                <ReviewConcernAccordion
                  key={reason.id}
                  reason={reason}
                  detail={detail}
                  footprint={footprint}
                  nested
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
